import uuid from 'uuid/v4';
import { encode, decode } from './encoder';
import { getEnv } from './env';
const packageVersion = "6.0.7";
const originParamName = 'atlOrigin';
const originVersioningAttributes = {
  originLibrary: `origin-tracing.js@${packageVersion}`
};
export default class OriginTracer {
  constructor({
    id = OriginTracer.generateId(),
    product,
    dangerouslySkipValidation = false
  }) {
    this.id = id;
    this.product = product;

    if (!dangerouslySkipValidation) {
      this.validate();
    }
  }

  static isValidId(value) {
    return typeof value === 'string' && /^[-_a-zA-Z0-9]{1,36}$/.test(value);
  }

  static isValidProduct(value) {
    return typeof value === 'string' && /^[-_a-zA-Z0-9]{1,20}$/.test(value);
  }

  static generateId() {
    const id = uuid(); // Dash positions are fixed so they don't add to uniqueness.
    // Remove them to save space in the encoded form.

    return id.replace(/-/g, '');
  }

  static createEmpty() {
    return new EmptyOriginTracer();
  }

  static createMalformed() {
    return new MalformedOriginTracer();
  }

  static fromUrl(url) {
    let params = new (getEnv().URLSearchParams)();
    replaceQuery(url, queryString => {
      params = new (getEnv().URLSearchParams)(queryString);
    });
    return OriginTracer.fromEncoded(params.get(originParamName));
  }

  static fromEncoded(encoded) {
    if (!encoded) {
      return OriginTracer.createEmpty();
    }

    try {
      return OriginTracer.decode(encoded);
    } catch (e) {
      return OriginTracer.createMalformed();
    }
  }

  static removeFromUrl(url) {
    return replaceQuery(url, queryString => {
      const params = new (getEnv().URLSearchParams)(queryString);
      params.delete(originParamName);
      return params.toString();
    });
  }

  static decode(encodedOrigin) {
    const originData = decode(encodedOrigin);
    return new OriginTracer(originData);
  }

  encode() {
    return encode({
      id: this.id,
      product: this.product
    });
  }

  addToUrl(url) {
    return replaceQuery(url, originalQueryString => {
      const params = new (getEnv().URLSearchParams)(originalQueryString);
      params.set(originParamName, this.encode());
      return params.toString();
    });
  }

  isEmpty() {
    return false;
  }

  isMalformed() {
    return !this.isEmpty() && !this.isValid();
  }

  isValid() {
    return true;
  }

  toAnalyticsAttributes({
    transformValue = v => v === null ? undefined : v,
    hasGeneratedId = false
  } = {}) {
    // `originIdGenerated` should be used in analytic events that generated this originId.
    // Subsequent events should use `originId`.
    // No two events should have the same value of originIdGenerated.
    const originIdProp = hasGeneratedId ? 'originIdGenerated' : 'originId';
    return {
      [originIdProp]: transformValue(this.id),
      originProduct: transformValue(this.product),
      ...originVersioningAttributes
    };
  }

  validate() {
    if (!OriginTracer.isValidId(this.id)) {
      throw new TypeError('Invalid Origin id');
    }

    if (!OriginTracer.isValidProduct(this.product)) {
      throw new TypeError('Missing/invalid Origin product');
    }
  }

}

class EmptyOriginTracer extends OriginTracer {
  constructor() {
    super({
      id: null,
      product: null,
      dangerouslySkipValidation: true
    });
  }

  isValid() {
    return false;
  }

  isEmpty() {
    return true;
  }

  toAnalyticsAttributes() {
    return { ...originVersioningAttributes
    };
  }

}

class MalformedOriginTracer extends OriginTracer {
  constructor() {
    super({
      id: null,
      product: null,
      dangerouslySkipValidation: true
    });
  }

  isValid() {
    return false;
  }

  isEmpty() {
    return false;
  }

  toAnalyticsAttributes() {
    return {
      originMalformed: true,
      ...originVersioningAttributes
    };
  }

}

function replaceQuery(url, replacer) {
  const urlWithQueryStringRegex = /(^[^?#]*)(?:\?)?([^#]*?)(#.*|$)$/; // If we get passed window.location, we don't want to call window.location.replace()

  const urlString = String(url);
  return urlString.replace(urlWithQueryStringRegex, (wholeMatch, beforeQuery, queryString, afterQuery) => {
    let newQueryString = replacer(queryString);

    if (newQueryString) {
      newQueryString = `?${newQueryString}`;
    }

    return `${beforeQuery}${newQueryString}${afterQuery}`;
  });
}